import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Form from 'antd/lib/form';
import Button from 'antd/lib/button';
import Spin from 'antd/lib/spin';
import message from 'antd/lib/message';
import { Row, Col } from 'react-flexbox-grid';
import { translate } from 'react-i18next';
import {
  accountRoles,
  accountRegistrationStatuses,
} from '../../../common/enums';
import { CreditorProfile, DebtorProfile, BiometricVerificationResult } from './profile';
import { AcceptDocumentsModal, RejectDocumentsModal } from '../components/documents';

// rc-form nests its error object for dotted field names, so the first real
// message can sit at any depth.
function firstErrorMessage(node) {
  if (!node || typeof node !== 'object') {
    return null;
  }
  if (Array.isArray(node.errors) && node.errors.length > 0) {
    return node.errors[0].message;
  }
  for (const key of Object.keys(node)) {
    const found = firstErrorMessage(node[key]);
    if (found) {
      return found;
    }
  }
  return null;
}

@translate()
@Form.create()
export default class AccountProfile extends Component {
  static propTyes = {
    t: PropTypes.func.isRequired,
    account: PropTypes.object.isRequired,
    profileEditable: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onImpersonate: PropTypes.func.isRequired,
    form: PropTypes.object.isRequired,
    isUpdating: PropTypes.bool.isRequired,
    isFetchingIdCardInfo: PropTypes.bool.isRequired,
    updateIdCardInfo: PropTypes.func.isRequired,
    onAcceptDocuments: PropTypes.func.isRequired,
    onRejectDocuments: PropTypes.func.isRequired,
    isCallCentrum: PropTypes.bool.isRequired,
    accountUpdateLogs: PropTypes.object,
    accountDocuments: PropTypes.array.isRequired,
    token: PropTypes.string.isRequired,
    canImpersonate: PropTypes.bool
  };

  handleSubmit = e => {
    e.preventDefault();

    const { form, onSubmit, t } = this.props;

    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        onSubmit(values);
        return;
      }
      // The obligations validator has no visible field to scroll to, so its
      // message has to travel in the notification itself.
      const detail = firstErrorMessage(err);
      message.error(
        detail
          ? `${t('Formulár sa neuložil')}: ${detail}`
          : t('Formulár sa neuložil, skontrolujte označené polia')
      );
    });
  };

  handleEdit = e => {
    e.preventDefault();
    const { account, onEdit } = this.props;
    onEdit(account.id);
  };

  handleImpersonate = e => {
    e.preventDefault();
    const {account,onImpersonate} = this.props;
    onImpersonate(account.id);
  }

  handleCancelEdit = e => {
    const { onEdit, form, account } = this.props;
    e.preventDefault();
    onEdit('');
    form.setFieldsValue({ emailsKeys: [...Array(account.emails.length).keys()] });
  };

  handleRejectDocumentsSubmit = rejectReason => this.props.onRejectDocuments(rejectReason);

  render() {
    const { account, profileEditable, isUpdating, form, isFetchingIdCardInfo, updateIdCardInfo, t, onAcceptDocuments, isCallCentrum, accountUpdateLogs, accountDocuments } = this.props;

    const isEditable = profileEditable === account.id;
    const tailFormItemLayout = {
      wrapperCol: {
        span: 14,
        offset: 6,
      },
    };

    const hasBiometricsData = account.verificationResult && account.verificationResult.result === 'FINISHED' && account.verificationResult.data;
    const biometricsData = hasBiometricsData ? account.verificationResult.data : {};

    return (
      <Row className="padded">
        <Col md={12} lg={hasBiometricsData ? 6: 12}>
          <h2 style={isCallCentrum ? { marginLeft: '1rem' } : {}}>
            {isCallCentrum ? `${account.nameFirst} ${account.nameLast}` : t('Osobné údaje')}
          </h2>
          {!!this.props.canImpersonate ? (<div style={{position:"absolute",top:"0px",right:"50px",display:"flex", zIndex:"100"}}>
            <Button size="small" type="primary" icon="user" onClick={this.handleImpersonate}>
              {t('Prihlásiť ako')}
            </Button>
          </div>) : null}
          <Spin spinning={isUpdating}>
            <Form onSubmit={this.handleSubmit}>

              {account.role === accountRoles.DEBTOR && (
                <DebtorProfile
                  account={account}
                  isEditable={isEditable}
                  form={form}
                  isFetchingIdCardInfo={isFetchingIdCardInfo}
                  updateIdCardInfo={updateIdCardInfo}
                  isCallCentrum={isCallCentrum}
                  accountUpdateLogs={accountUpdateLogs}
                />
              )}
              {account.role === accountRoles.CREDITOR && (
                <CreditorProfile
                  account={account}
                  isEditable={isEditable}
                  form={form} t={t}
                  isFetchingIdCardInfo={isFetchingIdCardInfo}
                  updateIdCardInfo={updateIdCardInfo}
                  isCallCentrum={isCallCentrum}
                  accountUpdateLogs={accountUpdateLogs}

                />
              )}

              <Form.Item {...tailFormItemLayout}>
                {!isEditable && (
                  <Button size="default" type="dashed" onClick={this.handleEdit}>
                    {t('Upraviť údaje')}
                  </Button>
                )}
                {isEditable && (
                  <Button size="default" type="primary" htmlType="submit">
                    {t('Uložiť')}
                  </Button>
                )}
                {isEditable && (
                  <Button size="default" className="btn-inline" onClick={this.handleCancelEdit}>
                    {t('Zrušiť úpravy')}
                  </Button>
                )}
              </Form.Item> 

              {!isEditable && !isCallCentrum &&
                account.registrationStatus === accountRegistrationStatuses.DOCUMENTS_SUBMITTED && (
                  <Form.Item {...tailFormItemLayout}>
                    <AcceptDocumentsModal onSubmit={onAcceptDocuments}>
                      <Button size="small" type="primary" icon="check">
                        {t('Schváliť OP')}
                      </Button>
                    </AcceptDocumentsModal>
                    <RejectDocumentsModal onSubmit={this.handleRejectDocumentsSubmit}>
                      <Button className="btn-inline" size="small" type="danger" icon="close-circle-o">
                        {t('Zamietnuť OP')}
                      </Button>
                    </RejectDocumentsModal>
                  </Form.Item>
                )}
            </Form>
          </Spin>
        </Col>
        {hasBiometricsData && (
          <Col sm={12} md={6}>
              <BiometricVerificationResult
                biometricsData={biometricsData}
                accountDocuments={accountDocuments}
                token={this.props.token}
              />
          </Col>
        )}
      </Row>
    );
  }
}



// WEBPACK FOOTER //
// ./src/features/account-management/components/AccountProfile.js